import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { TenantsLeasesSubNav } from "@/components/TenantsLeasesSubNav";
import { DataTable } from "@/components/table/DataTable";
import { SavedViewChips } from "@/components/table/SavedViewChips";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { ApiError, apiFetch } from "@/lib/api";
import type { LeaseListItemResource, Page } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { LEASE_STATUS_LABELS } from "@/lib/enum-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { useIsPhone } from "@/lib/use-media-query";

import { AddLeaseDialog } from "./AddLeaseDialog";

/**
 * `/tenants-leases/leases` (Story 6.7) — this codebase's first-ever
 * top-level, unscoped leases list: the shared `DataTable` (Epic 2), real
 * AD-14 sort/filter/paging against `GET /leases`, and real saved-views
 * support (`SavedViewChips`) exactly as `TenantsList.tsx`/`UsersList.tsx`
 * already established.
 *
 * A sibling path under the existing `/tenants-leases` nav destination
 * (mirrors `LeaseRecord.tsx`'s own `/tenants-leases/leases/:id` routing
 * doc comment) — not a tenth nav-rail destination of its own, and not the
 * bare `/tenants-leases` path itself, which stays the tenants list's own.
 *
 * `FILTERABLE_FIELDS` includes `in_force` alongside `leases_repo.
 * FILTERABLE_FIELDS`'s own `status`/`unit_id` (`app/api/leases.py`'s
 * `lease_list_query`) — the "Rent roll" saved view's own `in_force=true`
 * filter, so it is treated as a first-class, recognised part of this
 * list's own grammar (VC-6.7-08/-10) rather than merely forwarded through
 * `appendUnknownParams`'s "unrecognised passthrough" mechanism.
 *
 * No inline editing here: there is no generic `PATCH /leases/{id}` route
 * for a cell to write to (`LeaseRecord.tsx`'s own identical reasoning).
 *
 * Story 15.3 adds the real "Add lease" affordance — Owner-gated, mirroring
 * `PropertiesList.tsx`'s own `useIsOwner`-gated "Add a property" pattern
 * (spec AC/judgement call 4: this is the *only* entry point this story
 * builds — deliberately not also on `PropertyRecord.tsx`/`TenantRecord.
 * tsx`, since no Unit record page exists yet for either to link a lease's
 * own unit through). `AddLeaseDialog.tsx` carries the real form and submit
 * sequence; this file only wires it in and navigates to the new lease's own
 * record page on success, mirroring `PropertiesList.tsx`'s own `onCreated`
 * shape exactly.
 */

const FILTERABLE_FIELDS = ["status", "unit_id", "in_force"] as const;

const LEASES_LIST_KEY = "leases";

export function LeasesList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  //: `POST /leases` is `role=Role.OWNER` (`app/api/leases.py`) — mirrors
  //: `PropertiesList.tsx`'s own reasoning exactly: withheld from anyone but
  //: a confirmed Owner, not merely "not Read-only" (a Member is not
  //: Read-only and would otherwise see this button, then 403 the moment
  //: they submitted).
  const isOwner = useIsOwner();
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    document.title = "Leases — PropDesk";
  }, []);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["leases", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<LeaseListItemResource>>(`/leases?${search}`),
    placeholderData: keepPreviousData,
  });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["leases", "total"],
    queryFn: () => apiFetch<Page<LeaseListItemResource>>("/leases?page_size=1"),
    enabled: hasActiveFilters,
  });

  const columns: DataTableColumn<LeaseListItemResource>[] = [
    { key: "property_nickname", sortable: false },
    { key: "unit_label", sortable: false },
    {
      // Story 15.21 — the previously-missing "Tenants" column (Owner-caught
      // 2026-08-26: `GET /leases` carried no tenant field at all, so this
      // list had nothing to render). Positioned right after "Unit" — who is
      // renting follows immediately from which unit they are renting,
      // before the lease's own status/dates/money.
      //
      // `sortable: false` (spec judgement call 3): `leases_repo.
      // SORTABLE_FIELDS` has no `tenants` entry and never will — a lease's
      // co-tenants are an unordered, one-to-many set (`LeaseTenant` is an
      // uncapped many-to-many join, no "primary tenant" concept anywhere in
      // this codebase), so "sort by tenant name" has no well-defined single
      // key to sort on once a lease has more than one. Every other list's
      // own multi-valued column (`utilities_included`, say) is likewise
      // never sortable.
      //
      // Every tenant renders as its own separate `<Link>` — the exact
      // `/tenants-leases/{tenant_id}` pattern `LeaseRecord.tsx`'s own
      // "Co-tenants" section already establishes — comma-separated, with no
      // shared "comma-separated link list" primitive (spec judgement call
      // 2: this is this codebase's only caller so far). `onClick={(event)
      // => event.stopPropagation()}` on each `Link` mirrors `DocumentsList.
      // tsx`'s own identical guard: this column is not the row's identifier
      // (index 0), so without it a tenant click would also fire the row's
      // own `onClick` navigation to the lease record.
      //
      // Zero tenants (a real, legal state, VC-6.5-11: a lease is creatable
      // before anyone is attached) renders the honest, explicit "No
      // tenants" — never a dash that could read as "data not loaded," never
      // a fabricated name.
      key: "tenants",
      sortable: false,
      render: (row) =>
        row.tenants.length === 0 ? (
          "No tenants"
        ) : (
          <>
            {row.tenants.map((tenant, index) => (
              <span key={tenant.tenant_id}>
                {index > 0 ? ", " : null}
                <Link
                  to={`/tenants-leases/${tenant.tenant_id}`}
                  className="text-ink underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {tenant.full_name}
                </Link>
              </span>
            ))}
          </>
        ),
    },
    {
      key: "status",
      render: (row) => LEASE_STATUS_LABELS[row.status] ?? row.status,
    },
    {
      // Story 15.20 — the missing "Start date" column (Owner-caught gap: this
      // list already fetches `start_date` on every `LeaseListItemResource`,
      // it just never rendered a column for it). `start_date` is a required
      // field on every lease (`app/api/leases.py`), unlike `end_date`
      // (month-to-month leases carry `end_date: null`), so unlike the
      // `end_date` column immediately below, this one needs no `"—"`
      // fallback branch — `formatDateCell` always has a real value to
      // format. Positioned immediately before `end_date`, mirroring every
      // other screen in this app that shows both a lease's start and end
      // (`TenantsList.tsx`'s/`PropertiesList.tsx`'s own "Lease start"/"Lease
      // end" pairs, `LeaseRecord.tsx`'s own key-facts strip) — start always
      // reads before end.
      key: "start_date",
      tabularFigures: true,
      render: (row) => formatDateCell(row.start_date),
    },
    {
      key: "end_date",
      tabularFigures: true,
      render: (row) => (row.end_date ? formatDateCell(row.end_date) : "—"),
    },
    {
      key: "monthly_rent_cents",
      tabularFigures: true,
      align: "right",
      render: (row) => formatMoneyCell(row.monthly_rent_cents),
    },
    {
      key: "deposit_amount_cents",
      tabularFigures: true,
      align: "right",
      sortable: false,
      render: (row) => formatMoneyCell(row.deposit_amount_cents),
    },
  ];

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <TenantsLeasesSubNav />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Leases</h1>
          <p className="mt-2 text-body text-ink-secondary">
            Every lease in the portfolio. Select "Rent roll" for every lease currently in force,
            sorted by expiration.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-start gap-2">
          {isOwner ? (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              Add lease
            </Button>
          ) : null}
        </div>
      </div>
      <SavedViewChips listKey={LEASES_LIST_KEY} />
      <DataTable
        caption="Leases"
        itemNamePlural="leases"
        tint="cyan"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        getRowHref={(row) => `/tenants-leases/leases/${row.id}`}
        emptyState={
          isOwner
            ? {
                heading: "No leases yet.",
                body: "Add the first lease to start tracking it.",
                action: { label: "Add lease", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No leases yet.",
                body: "Leases will appear here once an Owner adds one.",
              }
        }
      />

      {isOwner ? (
        <AddLeaseDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/tenants-leases/leases/${id}`)}
        />
      ) : null}
    </div>
  );
}
